import * as React from "react"
import { useCallback } from "react"
import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"
import { MessageContent, useMessageContext } from "./message"
import { MessageImageProps } from "./message-image"
import { Download } from "lucide-react"
import { useAppDispatch } from "@/reducers/hooks"
import {
  downloadMultipleImagesAsZip,
  UrlAndFilename,
} from "@/components/message/utils/downloadMultipleImagesAsZip"
import {
  browserVersion,
  isMacOs,
  isMobile,
  isSafari,
  isWindows,
} from "@/utils/detectDevice"
import { updateImageGallery, updatePDFViewer } from "@/reducers/overlaysSlice"
import { mobileRoutes, setMobileRoute } from "@/routes/routes"
import { Message as MessageType, Thumbnail } from "@/reducers/messagesSlice"
import { mapFileUrlToSameOrigin } from "@/components/message/utils/mapFileToSameOrigin"
import { preventClick } from "@/utils/eventHelpers"

export interface GroupedFile
  extends Omit<MessageImageProps, "className" | "children"> {
  id: string
  message?: MessageType
  thumbnail?: Thumbnail | undefined
  filename: string
  alt?: string
  caption?: string
  pdf: boolean
}

export interface MessageImageGroupProps
  extends React.HTMLAttributes<HTMLDivElement> {
  images: GroupedFile[]
  maxImages?: number
  showCount?: boolean
  onImageClick?: (
    image: GroupedFile,
    index: number,
    event: React.MouseEvent,
  ) => void
  aspectRatio?: "square" | "auto"
  gap?: "sm" | "md" | "lg"
  onExpand?: () => void
  onDownload?: () => void
}

const MessageImageGroup = React.forwardRef<
  HTMLDivElement,
  MessageImageGroupProps
>(
  (
    {
      className,
      images,
      maxImages = 4,
      showCount = true,
      aspectRatio = "square",
      gap = "sm",
      children,
      onExpand,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation()
    const { interactive } = useMessageContext()
    const dispatch = useAppDispatch()
    const handleDownload = useCallback(() => {
      downloadMultipleImagesAsZip(
        images.map((image) => ({
          url: image.src ? mapFileUrlToSameOrigin(image.src) : image.src,
          filename: image.filename,
        })) as UrlAndFilename[],
      )
    }, [images])

    const handleImageClick = useCallback(
      (image: GroupedFile, index: number, event: React.MouseEvent) => {
        const isCtrlOrCmdClick =
          event.button === 0 &&
          ((isWindows && event.ctrlKey) || (isMacOs && event.metaKey))
        const isMiddleClick = event.button === 1
        if (isCtrlOrCmdClick || isMiddleClick) {
          window.open(image.src)
        } else {
          dispatch(
            updateImageGallery({
              visible: true,
              initialImage: image.src
                ? mapFileUrlToSameOrigin(image.src)
                : image.src,
            }),
          )
          if (isMobile) {
            setMobileRoute(mobileRoutes.GALLERY)
          }
        }
      },
      [dispatch],
    )

    const handlePDFClick = useCallback(
      (PDFMessage: MessageType, index: number, event: React.MouseEvent) => {
        const isOldSafari = isSafari && parseInt(browserVersion, 10) < 17
        const isCtrlOrCmdClick =
          event.button === 0 &&
          ((isWindows && event.ctrlKey) || (isMacOs && event.metaKey))
        const isMiddleClick = event.button === 1
        if (isCtrlOrCmdClick || isMiddleClick || isOldSafari) {
          window.open(PDFMessage.url)
        } else {
          dispatch(
            updatePDFViewer({
              visible: true,
              PDFMessage,
            }),
          )
          if (isMobile) {
            setMobileRoute(mobileRoutes.PDF)
          }
        }
      },
      [dispatch],
    )

    const handleClick = useCallback(
      (image: GroupedFile, index: number, event: React.MouseEvent) => {
        if (image.pdf && image.message) {
          handlePDFClick(image.message, index, event)
        } else {
          handleImageClick(image, index, event)
        }
      },
      [handleImageClick, handlePDFClick],
    )

    if (!images.length) return null

    const displayImages = images.slice(0, maxImages)
    const hiddenCount = Math.max(0, images.length - maxImages)

    const getGridLayout = (count: number) => {
      switch (count) {
        case 1:
          return "grid-cols-1"
        case 2:
          return "grid-cols-2"
        case 3:
          return "grid-cols-2 grid-rows-2"
        case 4:
        default:
          return "grid-cols-2 grid-rows-2"
      }
    }

    const getImageSpan = (index: number, total: number) => {
      // Special layout for 3 images: first image spans 2 rows
      if (total === 3 && index === 0) {
        return "row-span-2"
      }
      return ""
    }

    const getGapClass = () => {
      switch (gap) {
        case "sm":
          return "gap-1"
        case "md":
          return "gap-2"
        case "lg":
          return "gap-3"
        default:
          return "gap-1"
      }
    }
    return (
      <MessageContent ref={ref} {...props}>
        <div className="mb-1 flex flex-row justify-between gap-x-2 text-xs text-black dark:text-white">
          <div className="flex flex-row gap-x-1 text-sm font-normal leading-normal text-accent-foreground">
            <span>
              {t("chat.imageGroup.fileCount", { count: images.length })}
            </span>
            <span>|</span>
            <a
              href="#"
              className="cursor-pointer text-sm font-normal leading-normal text-accent-foreground text-base-primary"
              onClick={interactive ? preventClick(onExpand) : undefined}
            >
              {t("chat.imageGroup.showAll")}
            </a>
          </div>

          <button
            type="button"
            disabled={!interactive}
            onClick={interactive ? handleDownload : undefined}
            className="cursor-pointer text-sm font-normal leading-normal text-accent-foreground text-base-primary"
          >
            <div className="flex items-center gap-2">
              {t("chat.media.download")} <Download className="size-4" />
            </div>
          </button>
        </div>

        <div
          className={cn(
            "grid max-w-60 overflow-hidden rounded-lg",
            getGridLayout(displayImages.length),
            getGapClass(),
            className,
          )}
        >
          {displayImages.map((image, index) => (
            <div
              key={image.id}
              className={cn(
                "group/grid-image relative cursor-pointer overflow-hidden rounded-lg bg-muted",
                aspectRatio === "square" ? "aspect-square" : "aspect-auto",
                getImageSpan(index, displayImages.length),
              )}
              onMouseDown={
                interactive
                  ? (event) => handleClick?.(image, index, event)
                  : undefined
              }
            >
              <img
                src={
                  image.pdf
                    ? image.thumbnail?.uri
                    : image && image.src && mapFileUrlToSameOrigin(image?.src)
                }
                alt={
                  image.alt ||
                  t("chat.imageGroup.fileAlt", { number: index + 1 })
                }
                className="h-full w-full rounded-lg object-cover transition-transform group-hover/grid-image:scale-105"
                loading="lazy"
              />
              {image.pdf && (
                <div className="absolute left-1 top-1 rounded bg-black/60 p-0.5">
                  <img src="/pdf.svg" alt="PDF" className="h-4 w-4" />
                </div>
              )}
              {/* Show count overlay on last image if there are hidden images */}
              {index === displayImages.length - 1 &&
                hiddenCount > 0 &&
                showCount && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                    <span className="text-lg font-semibold text-white">
                      +{hiddenCount}
                    </span>
                  </div>
                )}

              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/0 transition-colors group-hover/grid-image:bg-black/20" />
            </div>
          ))}
        </div>

        {children}
      </MessageContent>
    )
  },
)

MessageImageGroup.displayName = "MessageImageGroup"

export { MessageImageGroup }
